import React, { useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import close from '../../../assets/images/close.svg';
import { MOCK_PAY_DAY_OFFERS as data } from '../../../api/mock.data.js';
import starAssets from '../../StarPage/imagePage';
import useOnClickOutside from '../../../hooks/useOnClickOutside.js';
import paydayAssets from './payDayImages';
import {
  callFlutterExternalLinkHandler,
  callFlutterHandler,
} from '../../../utils/flutterBridge.js';
import { ctLogV2 } from '../../../utils/cleverTapLogger.utils.js';
import { useIdHash } from '../../../hooks/custom.hooks.js';
import { useSelector } from 'react-redux';
import ShimmerSkeleton from './Shimmer/index.jsx';

function PayDay() {
  const { custId: cid } = useIdHash();
  const [activeTab, setActiveTab] = useState('Online');
  const [activeIndex, setActiveIndex] = useState(null);
  const fqRef = useRef(null);
  const faqScrollRef = useRef(null);
  const imageLoading = useSelector(
    ({ appReducer }) => appReducer.imagesLoading,
  );

  const handleCategoryClick = (item) => {
    handleEventCall(item);
    faqScrollRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useOnClickOutside(fqRef, () => {
    setActiveIndex(null);
  });

  useEffect(() => {
    if (cid) {
      logPaydayEvent({
        eventName: 'AppWebpageViewed',
        customerId: cid,
      });
    }
  }, [cid]);

  const logPaydayEvent = ({
    eventName,
    customerId,
    bannerName,
    bannerPosition,
    type,
  }) => {
    ctLogV2({
      eventName: eventName,
      customerId: customerId,
      extraData: {
        source: 'paydaysale',
        ...(type && { type }),
        ...(bannerPosition && {
          banner_position: bannerPosition,
        }),
        ...(bannerName && { banner_name: bannerName }),
      },
    });
  };

  const stepData = {
    Online: [
      {
        icon: paydayAssets?.online?.onlinestep1,
        title:
          "Click on the 'Explore' button on the homepage to checkout our partner brands",
        eventName: 'AppWebpageElementClicked',
        type: 'HowtoUseRewards',
        name: 'Online',
      },
      {
        icon: paydayAssets?.online?.onlinestep2,
        title: 'Add your favorites to the cart.',
        eventName: 'AppWebpageElementClicked',
        type: 'HowtoUseRewards',
        name: 'Online',
      },
      {
        icon: paydayAssets?.online?.onlinestep3,
        title:
          'At checkout, select Pay with Rewards to use your points and unlock extra discounts.',
        eventName: 'AppWebpageElementClicked',
        type: 'HowtoUseRewards',
        name: 'Online',
      },
    ],
    Offline: [
      {
        icon: paydayAssets?.offline?.offlinestep1,
        title:
          "Click on the 'Explore' button on the homepage to checkout our partner brands",
        eventName: 'AppWebpageElementClicked',
        type: 'HowtoUseRewards',
        name: 'Offline',
      },
      {
        icon: paydayAssets?.offline?.offlinestep2,
        title: 'At the checkout counter, scan the QR code using your TWID App.',
        eventName: 'AppWebpageElementClicked',
        type: 'HowtoUseRewards',
        name: 'Offline',
      },
      {
        icon: paydayAssets?.offline?.offlinestep3,
        title: 'Use your reward points to get instant discounts on the bill.',
        eventName: 'AppWebpageElementClicked',
        type: 'HowtoUseRewards',
        name: 'Offline',
      },
    ],
  };
  const steps = stepData[activeTab];

  const twidIntro = [
    {
      img: paydayAssets?.userInfo?.usercount,
      title: '10M+',
      sub_title: 'users',
    },
    {
      img: paydayAssets?.userInfo?.rewardSaved,
      title: '₹100Cr+',
      sub_title: 'Rewards Saved',
    },
    {
      img: paydayAssets?.userInfo?.rated,
      title: '4.8★',
      sub_title: 'Rated App',
    },
  ];

  const faqList = [
    {
      question: 'What are Payday Deals?',
      answer:
        'Payday Deals are special offers that help you save more on shopping, food, travel, movies, bill payments, and more – right when your salary hits.',
      eventName: 'AppWebpageElementClicked',
      type: 'FAQ',
      name: 'What are Payday Deals?',
    },
    {
      question: 'How long are Payday Deals valid?',
      answer:
        'The campaign runs for 9 days starting from the 28th March to 5th April',
      eventName: 'AppWebpageElementClicked',
      type: 'FAQ',
      name: 'How long are Payday Deals valid?',
    },
    {
      question: 'Where can I use these deals?',
      answer:
        'You can shop across 400+ partner merchants including Swiggy, JioMart, Confirmtkt, BookMyShow, ixigo, Reliance, and many more.',
      eventName: 'AppWebpageElementClicked',
      type: 'FAQ',
      name: 'Where can I use these deals?',
    },
    {
      question: 'How do I redeem my rewards?',
      answer: (
        <ul>
          <li>
            <b>Online:</b> Choose your favourite brand → Add your preferred
            items to cart → Choose Pay with Rewards (TWID UPI) during checkout.
          </li>
          <br />
          <li>
            <b>Offline:</b> Walk into a partner store → Shop for essentials →
            Pay using TWID UPI and earn rewards/save on your purchases with
            instant discounts.
          </li>
        </ul>
      ),
      eventName: 'AppWebpageElementClicked',
      type: 'FAQ',
      name: 'How do I redeem my rewards?',
    },
    {
      question: 'Can I combine Payday Deals with other offers?',
      answer:
        'Yes, in many cases you can combine Payday Deals with bank/card offers for extra savings. (T&Cs apply).',
      eventName: 'AppWebpageElementClicked',
      type: 'FAQ',
      name: 'Can I combine Payday Deals with other offers?',
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleEventCall = (item) => {
    logPaydayEvent({
      eventName: item?.eventName,
      customerId: cid,
      type: item?.type,
      bannerPosition: item?.position,
      bannerName: item?.name,
    });
    if (item?.redirectUrl) {
      if (!item?.inApp) {
        callFlutterExternalLinkHandler('openLinkExternal', {
          url: item?.redirectUrl,
        });
      } else if (item?.inApp) {
        callFlutterHandler('backButton');
      }
    }
  };

  if (imageLoading) {
    return <ShimmerSkeleton />;
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.headerSection}>
        <div className={styles.heading}>Offer Details</div>
        <img
          src={close}
          alt="close"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            callFlutterHandler('backButton');
          }}
        />
      </div>
      <div className={styles.contentSection}>
        {/* banner image  done  */}
        <div className={styles.banner}>
          <img src={data?.payday_banner?.imageUrl} alt="Pay Day Banner" />
        </div>
        {/* categories done */}
        <div className={styles.categoriesSection}>
          <div className={styles.heading}>
            Your Favorite brands, Your Daily Savings
          </div>
          <div className={styles.subHeading}>
            Pay with TWID UPI and turn your online shopping into instant
            savings.
          </div>
          <div className={styles.img}>
            {data?.categories?.map((category, index) => (
              <div
                key={index}
                className={styles.category}
                onClick={() => handleCategoryClick(category)}
              >
                <img src={category?.imageUrl} alt={`Category ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>
        {/* Exclusive */}
        <div className={styles.exclusiveSection}>
          <div className={styles.heading}>Rewards Waiting at Every Store</div>
          <div className={styles.subHeading}>
            Turn routine purchases into rewarding moments with TWID UPI
          </div>
          <div className={styles.img}>
            {data?.rewards?.map((exclusive, index) => (
              <div
                key={index}
                className={styles.exclusive}
                onClick={() => handleEventCall(exclusive)}
              >
                <img src={exclusive?.imageUrl} alt={`exclusive ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Brand Spotlist  */}
        <div
          className={styles.brandSplotlightSection}
          style={{
            backgroundImage: `url(${paydayAssets?.bgImage})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          <div className={styles.heading}>Extra Savings</div>
          <div className={styles.subHeading}>On the top brands</div>
          <div className={styles.img}>
            {data?.brandsInSpotlight?.map((brand, index) => (
              <div
                key={index}
                className={styles.brand}
                onClick={() => handleEventCall(brand)}
              >
                <img src={brand?.imageUrl} alt={`brand ${index + 1}`} />
              </div>
            ))}
          </div>
          <div className={styles.brandImg}>
            <div className={styles.brandHeading}>OTHER BRANDS</div>
            <div className={styles.imgContainer}>
              {data?.otherBrands?.map((brand, index) => (
                <div
                  key={index}
                  className={styles.brand}
                  onClick={() => handleEventCall(brand)}
                >
                  <img src={brand?.logoUrl} alt={`brand ${index + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* verified */}
        <div className={styles.verifiedSection}>
          {data?.verifiedBy?.map((item, index) => (
            <div key={index} className={styles.verified}>
              <img src={item?.logoUrl} alt={`verified ${index + 1}`} />
            </div>
          ))}
        </div>

        {/* unlock rewards */}
        <div className={styles.categoriesSection}>
          <div className={styles.heading}> App Exclusive Deals</div>
          <div className={styles.subHeading}>
            at every online brand & offline store
          </div>
          <div className={styles.img}>
            {data?.app?.map((category, index) => (
              <div
                key={index}
                className={styles.category}
                onClick={() => handleEventCall(category)}
              >
                <img src={category?.logoUrl} alt={`Category ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>
        {/* How to redeem star */}
        <div ref={faqScrollRef} className={styles.redeemContent}>
          <div className={styles.title}>How to use Rewards</div>

          <div className={styles.tabs}>
            {['Online', 'Offline'].map((tab) => (
              <button
                key={tab}
                className={`${styles.tab} ${activeTab === tab ? styles.active : ''}`}
                onClick={() => {
                  setActiveTab(tab);
                  handleEventCall(tab);
                }}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className={styles.steps}>
            {steps?.map((step, index) => (
              <div key={index} className={styles.step}>
                <div className={styles.iconWrapper}>
                  <img src={step.icon} alt={`step-${index + 1}`} />
                  {index !== steps.length - 1 && (
                    <div className={styles.dottedLine} />
                  )}
                </div>
                <div className={styles.text}>
                  <div className={styles.stepNumber}>STEP {index + 1}</div>
                  <div className={styles.stepTitle}>{step.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* star intro */}
        <div
          className={styles.introContainer}
          style={{
            backgroundImage: `url(${paydayAssets?.metric})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          <div className={styles.AboutStarContainer}>
            {twidIntro?.map((elem, index) => {
              const isOdd = index % 2 !== 0;
              return (
                <div
                  key={index}
                  className={`${styles.renderCard} ${isOdd ? styles.oddCard : ''}`}
                >
                  <img src={elem.img} alt={elem.title} />
                  <div>{elem?.title}</div>
                  <div>{elem?.sub_title}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/*  Frequent Asked question */}
        <div className={styles.fqContainer}>
          <div className={styles.heading}>Got Questions?</div>
          <div ref={fqRef} className={styles.listContainer}>
            {faqList.map((item, index) => (
              <div key={index} className={styles.section}>
                <div
                  onClick={() => {
                    toggleAccordion(index);
                    handleEventCall(item);
                  }}
                  className={styles.question}
                >
                  <div style={{ width: '85%' }}>{item.question}</div>
                  <img
                    src={starAssets.down}
                    alt="down_img"
                    style={{
                      transform:
                        activeIndex === index
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </div>
                {activeIndex === index && (
                  <div className={styles.answer} style={{ width: '100%' }}>
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PayDay;
